import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  message,
  Popconfirm,
  Typography,
  Alert,
  Tooltip,
  DatePicker,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useCan } from '@refinedev/core';
import dayjs from 'dayjs';
import { adminApi } from '@/services/api';
import { PerBuildConfigGuide } from './PerBuildConfigGuide';
import type {
  AppVersionMapping,
  CreateAppVersionMapping,
  ConfigVersion,
  AppVersionPlatform,
} from '@/types';

const { Text, Title } = Typography;

// Mirrors the backend SEMVER_REGEX on the app-version DTOs.
const DOTTED_NUMERIC = /^\d+(\.\d+)*$/;

/** Column header with a short explanation, so the table is self-describing. */
const colTitle = (label: string, help: string) => (
  <Tooltip title={help}>
    <span>
      {label} <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
    </span>
  </Tooltip>
);

/** Sort by parsed version, not lexicographically — else 0.10.0 sorts before 0.2.0. */
const compareVersions = (a: string, b: string): number => {
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
};

const PLATFORM_COLORS: Record<AppVersionPlatform, string> = {
  all: 'default',
  android: 'green',
  ios: 'blue',
};

interface FormValues extends Omit<CreateAppVersionMapping, 'releasedAt'> {
  releasedAt?: dayjs.Dayjs;
}

export const AppVersionsTab: React.FC = () => {
  const [rows, setRows] = useState<AppVersionMapping[]>([]);
  const [configVersions, setConfigVersions] = useState<ConfigVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AppVersionMapping | null>(null);
  const [saving, setSaving] = useState(false);
  const [purging, setPurging] = useState(false);
  const [form] = Form.useForm<FormValues>();
  const platform = Form.useWatch('platform', form);

  const { data: canEdit } = useCan({ resource: 'config', action: 'edit' });
  const editable = canEdit?.can !== false;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rowsRes, cvRes] = await Promise.all([
        adminApi.getBuildConfigVersions(),
        adminApi.getConfigVersions(),
      ]);
      setRows(
        [...(rowsRes.data ?? [])].sort((a, b) => compareVersions(a.buildVersion, b.buildVersion))
      );
      setConfigVersions(cvRes.data ?? []);
      setAvailable(true);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setAvailable(false);
      } else {
        message.error('Failed to load app versions');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true, platform: 'all' });
    setModalOpen(true);
  };

  const openEdit = (row: AppVersionMapping) => {
    setEditing(row);
    form.setFieldsValue({
      buildVersion: row.buildVersion,
      buildNumber: row.buildNumber ?? undefined,
      platform: row.platform,
      configVersionId: row.configVersionId,
      isActive: row.isActive,
      releasedAt: row.releasedAt ? dayjs(row.releasedAt) : undefined,
      note: row.note ?? undefined,
    });
    setModalOpen(true);
  };

  const apiError = (error: unknown, fallback: string) => {
    const detail = (error as { response?: { data?: { message?: string | string[] } } })?.response
      ?.data?.message;
    message.error(Array.isArray(detail) ? detail.join(', ') : (detail ?? fallback));
  };

  const handleSubmit = async (values: FormValues) => {
    setSaving(true);
    try {
      const payload: CreateAppVersionMapping = {
        buildVersion: values.buildVersion,
        buildNumber: values.buildNumber ?? undefined,
        platform: values.platform,
        configVersionId: values.configVersionId,
        isActive: values.isActive,
        releasedAt: values.releasedAt ? values.releasedAt.toISOString() : undefined,
        note: values.note || undefined,
      };
      if (editing) {
        await adminApi.updateBuildConfigVersion(editing.id, payload);
        message.success(`App version ${payload.buildVersion} updated`);
      } else {
        await adminApi.createBuildConfigVersion(payload);
        message.success(`App version ${payload.buildVersion} mapped`);
      }
      setModalOpen(false);
      await fetchAll();
    } catch (error) {
      apiError(error, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: AppVersionMapping) => {
    try {
      await adminApi.deleteBuildConfigVersion(row.id);
      message.success(`App version ${row.buildVersion} unmapped`);
      await fetchAll();
    } catch (error) {
      apiError(error, 'Delete failed');
    }
  };

  const handleToggleActive = async (row: AppVersionMapping, isActive: boolean) => {
    try {
      await adminApi.updateBuildConfigVersion(row.id, { isActive });
      message.success(isActive ? 'Mapping enabled' : 'Mapping disabled');
    } catch (error) {
      apiError(error, 'Update failed');
    } finally {
      await fetchAll();
    }
  };

  /** Copy a row onto a new build — the usual move when shipping a hotfix. */
  const handleDuplicate = (row: AppVersionMapping) => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      buildVersion: '',
      buildNumber: undefined,
      platform: row.platform,
      configVersionId: row.configVersionId,
      isActive: true,
      note: row.note ?? undefined,
    });
    setModalOpen(true);
  };

  const handlePurge = async () => {
    setPurging(true);
    try {
      const res = await adminApi.purgeConfigCache();
      message.success(`Cached config cleared on all instances (${res.data.keysCleared} keys)`);
    } catch (error) {
      apiError(error, 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  const columns = [
    {
      title: colTitle(
        'App Version',
        'What the app reports as its version, e.g. 1.0.2. Matched exactly — 0.2 and 0.2.0 are the same build.'
      ),
      dataIndex: 'buildVersion',
      key: 'buildVersion',
      render: (v: string) => <Text strong>{v}</Text>,
    },
    {
      title: colTitle(
        'Build No.',
        'Android versionCode / iOS build number. Older clients send only this. A dash means clients sending just a build number cannot be matched to this row.'
      ),
      dataIndex: 'buildNumber',
      key: 'buildNumber',
      render: (n: number | null) =>
        n ?? (
          <Tooltip title="No integer build number recorded. Clients that send only x-build-version cannot be matched to this row.">
            <Text type="secondary">—</Text>
          </Tooltip>
        ),
    },
    {
      title: colTitle(
        'Platform',
        'A platform-specific row wins over an "all" row for the same version. A build number needs a specific platform, since the stores number builds independently.'
      ),
      dataIndex: 'platform',
      key: 'platform',
      render: (p: AppVersionPlatform) => <Tag color={PLATFORM_COLORS[p]}>{p}</Tag>,
    },
    {
      title: colTitle('Config Version', 'The settings bundle players on this build receive.'),
      key: 'configVersion',
      render: (_: unknown, row: AppVersionMapping) => (
        <Tag color="blue">{row.configVersion?.version ?? row.configVersionId}</Tag>
      ),
    },
    {
      title: colTitle('Released', 'When this build reached players. Informational only.'),
      dataIndex: 'releasedAt',
      key: 'releasedAt',
      render: (d: string | null) =>
        d ? dayjs(d).format('YYYY-MM-DD') : <Text type="secondary">—</Text>,
    },
    {
      title: colTitle(
        'Active',
        'Turn off to stop applying this mapping without deleting it. Players on that build fall back to the global config.'
      ),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean, row: AppVersionMapping) => (
        <Switch
          size="small"
          checked={isActive}
          disabled={!editable}
          onChange={(checked) => handleToggleActive(row, checked)}
        />
      ),
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      render: (note: string | null) => note || <Text type="secondary">—</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, row: AppVersionMapping) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            disabled={!editable}
            onClick={() => openEdit(row)}
          >
            Edit
          </Button>
          <Tooltip title="Create the next build with the same settings">
            <Button
              size="small"
              icon={<CopyOutlined />}
              disabled={!editable}
              onClick={() => handleDuplicate(row)}
            />
          </Tooltip>
          <Popconfirm
            title="Unmap this app version?"
            description="Clients on this build fall back to the global config."
            onConfirm={() => handleDelete(row)}
            disabled={!editable}
          >
            <Button size="small" danger icon={<DeleteOutlined />} disabled={!editable} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!available) {
    return (
      <Alert
        type="warning"
        showIcon
        message="App version API not available"
        description="This backend does not expose /admin/config/build-versions yet."
      />
    );
  }

  return (
    <Card loading={loading}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <PerBuildConfigGuide
          current="app-versions"
          configVersionCount={configVersions.length}
          appVersionCount={rows.length}
        />

        <div>
          <Title level={5}>App Version → Config Version</Title>
          <Text type="secondary">
            Each row pins one released client build to the config version its players receive. A
            build with no row here — and any client that sends no version header — gets the global
            configuration from the Force Update tab, unchanged.
          </Text>
        </div>

        {rows.length === 0 && !loading && (
          <Alert
            type="info"
            showIcon
            message="No app versions mapped — every client gets the global config"
            description="That is the safe default. To give one build its own settings: create a config version on the previous tab, then map the build to it here, then set that config version on the content you want limited to it."
          />
        )}

        {configVersions.length === 0 && !loading && (
          <Alert
            type="warning"
            showIcon
            message="No config versions exist yet"
            description="Create one on the Config Versions tab first — an app version has to point at something."
          />
        )}

        <Space wrap>
          <Tooltip title={editable ? '' : 'You have read-only access to config'}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreate}
              disabled={!editable || configVersions.length === 0}
            >
              Map App Version
            </Button>
          </Tooltip>
          <Tooltip title="Config caches clear automatically on every save. Use this only if you changed something through another path and want the rebuild now instead of within the hour.">
            <Button onClick={handlePurge} loading={purging} disabled={!editable}>
              Rebuild cached config
            </Button>
          </Tooltip>
        </Space>

        <Table
          rowKey="id"
          dataSource={rows}
          columns={columns}
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
        />
      </Space>

      <Modal
        title={editing ? `Edit app version ${editing.buildVersion}` : 'Map an app version'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        okText={editing ? 'Save' : 'Create'}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="buildVersion"
            label="App Version"
            rules={[
              { required: true, message: 'Required' },
              { pattern: DOTTED_NUMERIC, message: 'Use dotted numbers, e.g. 1.0.2' },
            ]}
            extra="What the app reports as its version. Matched exactly; 0.2 and 0.2.0 are the same build."
          >
            <Input placeholder="1.0.2" />
          </Form.Item>

          <Form.Item
            name="platform"
            label="Platform"
            rules={[{ required: true, message: 'Required' }]}
            extra="A build number needs a specific store, because Android and iOS number builds independently."
          >
            <Select
              options={[
                { value: 'all', label: 'All platforms' },
                { value: 'android', label: 'Android' },
                { value: 'ios', label: 'iOS' },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="buildNumber"
            label="Build Number"
            rules={[{ type: 'number', min: 2, message: 'Must be 2 or more' }]}
            extra={
              platform === 'all'
                ? 'Choose Android or iOS to set a build number.'
                : 'Android versionCode / iOS CFBundleVersion. Must be 2 or more: every header parser reads a missing value as 1, so 1 cannot identify a real build.'
            }
          >
            <InputNumber
              min={2}
              style={{ width: '100%' }}
              placeholder="152"
              disabled={platform === 'all'}
            />
          </Form.Item>

          <Form.Item
            name="configVersionId"
            label="Config Version"
            rules={[{ required: true, message: 'Required' }]}
            extra="What this build's players receive."
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={configVersions.map((cv) => ({
                value: cv.id,
                label: cv.description ? `${cv.version} — ${cv.description}` : cv.version,
              }))}
            />
          </Form.Item>

          <Form.Item name="releasedAt" label="Released">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch checkedChildren="ON" unCheckedChildren="OFF" />
          </Form.Item>

          <Form.Item name="note" label="Note">
            <Input placeholder="e.g. holds pre-Blitz clients on 1.0.x" maxLength={255} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default AppVersionsTab;
